import { HEROES, type HeroDefinition } from '@fukemon/shared';
import { Billboard, useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { DoubleSide, type Group } from 'three';
import { useGameStore } from '../state/gameStore';

const FIGHTER_ART: Partial<Record<HeroDefinition['id'], string>> = {
  cuntmander: '/assets/fighters/cuntmander.png',
  dickachu: '/assets/fighters/dickachu.png',
  blowmesaur: '/assets/fighters/blowmesaur.png'
};

export function Creature({
  hero,
  scale = 1,
  animate = true,
  hostile = false
}: {
  hero: HeroDefinition;
  scale?: number;
  animate?: boolean;
  hostile?: boolean;
}) {
  const group = useRef<Group>(null);
  const exactFighterArt = FIGHTER_ART[hero.id];
  const texture = useTexture(exactFighterArt ?? hero.cardImage);
  const dimensions = useMemo(() => {
    if (!exactFighterArt) return [1.45, 2] as const;
    const image = texture.image as { width?: number; height?: number };
    const aspect = (image.width ?? 1) / (image.height ?? 1);
    return [2.25 * aspect, 2.25] as const;
  }, [exactFighterArt, texture]);

  useFrame(({ clock }) => {
    if (!group.current || !animate) return;
    group.current.rotation.z = Math.sin(clock.elapsedTime * 4.5) * 0.025;
    group.current.position.y = Math.abs(Math.sin(clock.elapsedTime * 4.5)) * 0.08;
  });

  return (
    <group ref={group} scale={scale}>
      <Billboard follow lockX={false} lockY={false} lockZ={false}>
        {hostile && (
          <mesh position={[0, 0.95, -0.04]} scale={[1.12, 1.08, 1]}>
            <planeGeometry args={[dimensions[0], dimensions[1]]} />
            <meshBasicMaterial color="#ff174e" transparent opacity={0.55} side={DoubleSide} />
          </mesh>
        )}
        <mesh position={[0, 0.95, 0]}>
          <planeGeometry args={[dimensions[0], dimensions[1]]} />
          <meshBasicMaterial map={texture} transparent alphaTest={0.12} side={DoubleSide} />
        </mesh>
      </Billboard>
    </group>
  );
}

export function ActiveCreature() {
  const heroId = useGameStore((state) => state.selectedHero);
  const hero = useMemo(() => HEROES.find((item) => item.id === heroId)!, [heroId]);
  return <Creature hero={hero} />;
}
